import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PILLAR_GUIDE, IDENTITY_GUIDE, AURA_GUIDE, SOCIETY_GUIDE } from '../../data/tileGuide';

const GOLD = '#D4AF37';
const GOLD_HI = '#F5D97A';
const AURA = ['#FF6478', '#5BE08A', '#5DD4FF', '#B978FF', '#7C9BFF', '#FFB265'];
const txt = { fontSize: 12.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.78)' };

export const SectionTitle = ({ children, sub }) => (
  <div className="pt-2 pb-0.5 px-1">
    <div className="font-['Cinzel'] uppercase" style={{ color: GOLD_HI, fontSize: 12, letterSpacing: '0.28em' }}>{children}</div>
    {sub && <div className="font-['Cormorant_Garamond'] italic" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{sub}</div>}
  </div>
);

const Fold = ({ id, title, tag, accent, lead, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden" data-testid={`menu-intro-${id}`}
      style={{ background: 'linear-gradient(180deg, rgba(20,16,8,0.9), rgba(8,7,12,0.95))', border: `1px solid ${open ? accent : 'rgba(212,175,55,0.18)'}`,
        boxShadow: open ? `0 0 18px ${accent}33` : 'none', transition: 'border-color 0.2s, box-shadow 0.2s' }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-3 py-3 text-left" data-testid={`menu-intro-toggle-${id}`}>
        <span className="w-11 h-11 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center"
          style={{ background: '#0b0a10', border: `1px solid ${accent}`, boxShadow: `0 0 10px ${accent}44` }}>{lead}</span>
        <span className="flex-1 min-w-0">
          <span className="block font-['Cinzel'] uppercase" style={{ fontSize: 13.5, letterSpacing: '0.14em', color: '#fff' }}>{title}</span>
          <span className="block font-['Cormorant_Garamond'] italic" style={{ fontSize: 14, color: accent }}>{tag}</span>
        </span>
        <ChevronDown size={16} color={GOLD_HI} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}/>
      </button>
      {open && <div className="px-4 pb-4 -mt-1"><div className="h-px mb-2" style={{ background: `linear-gradient(90deg, transparent, ${accent}66, transparent)` }}/>{children}</div>}
    </div>
  );
};

const Labelled = ({ label, text, color = GOLD_HI }) => (
  <div className="mt-2">
    <div className="font-['Outfit'] uppercase" style={{ fontSize: 9, letterSpacing: '0.24em', color }}>{label}</div>
    <p className="font-['Outfit'] mt-0.5" style={txt}>{text}</p>
  </div>
);

const Img = ({ src }) => <img src={src} alt="" className="w-full h-full object-cover"/>;

const AuraMini = () => (
  <svg viewBox="0 0 44 44" width="40" height="40">
    <path d="M 4 22 A 18 18 0 0 1 40 22" stroke={GOLD} strokeWidth="3" fill="none"/>
    {AURA.map((c, i) => {
      const a0 = Math.PI * (i / 6) + 0.05; const a1 = Math.PI * ((i + 1) / 6) - 0.05;
      const p = (a) => `${22 + 18 * Math.cos(a)} ${22 + 18 * Math.sin(a)}`;
      return <path key={c} d={`M ${p(a0)} A 18 18 0 0 1 ${p(a1)}`} stroke={c} strokeWidth="3" fill="none"/>;
    })}
  </svg>
);

/* Intro sections shown above the 24 tiles: 8 pillars, identity sigil, Aura ring. */
export const GuideIntro = () => (
  <>
    <SectionTitle sub="Tag every vibe to the journey it belongs to">The 8 Pillars</SectionTitle>
    {PILLAR_GUIDE.map((p) => (
      <Fold key={p.id} id={`pillar-${p.id}`} title={p.name} tag={`The ${p.animal}`} accent={p.accent} lead={<Img src={p.img}/>}>
        <p className="font-['Outfit']" style={txt}>{p.text}</p>
        <p className="font-['Outfit'] mt-2" style={{ ...txt, color: 'rgba(255,255,255,0.55)', fontSize: 11.5 }}>
          Upload a vibe and tag it {p.name}. It is collected into your Vault, Sovereign, Essence and Mosaic under this pillar.
        </p>
      </Fold>
    ))}
    <SectionTitle sub="Your identity mark">Profile Sigil</SectionTitle>
    <Fold id="identity" title="Your Sigil" tag="Why you choose one of 8" accent={GOLD} lead={<Img src="/icons/empire_seal.png"/>}>
      <Labelled label="What it is" text={IDENTITY_GUIDE.what}/>
      <Labelled label="How it works" text={IDENTITY_GUIDE.how}/>
      <Labelled label="Why it matters" text={IDENTITY_GUIDE.why}/>
    </Fold>
    <SectionTitle sub="The ring around your profile photo">Aura Ring</SectionTitle>
    <Fold id="aura-ring" title="6 Aura Lines + Gold" tag="How fusing works" accent="#B978FF" lead={<AuraMini/>}>
      {AURA_GUIDE.map((a) => <Labelled key={a.label} label={a.label} text={a.text}/>)}
    </Fold>
    <SectionTitle sub="24 rituals · what, how & why">The Rituals</SectionTitle>
  </>
);

/* Bliss Society section shown after the last tile (Mosaic). */
export const GuideSociety = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <>
      <SectionTitle sub="Society of Successful Empires">Bliss Society</SectionTitle>
      <Fold id="society" title="Bliss Society" tag="What's inside" accent={GOLD_HI} lead={<Img src="/icons/bliss_icon.png"/>}>
        <p className="font-['Outfit']" style={txt}>{SOCIETY_GUIDE.intro}</p>
        {SOCIETY_GUIDE.parts.map((s) => <Labelled key={s.label} label={s.label} text={s.text}/>)}
        <button onClick={() => { onClose(); navigate('/discover'); }} data-testid="menu-intro-open-society"
          className="mt-3 w-full py-2.5 rounded-full font-['Cinzel'] uppercase active:scale-[0.98]"
          style={{ background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0806', fontSize: 11, letterSpacing: '0.24em', fontWeight: 700 }}>
          Enter Bliss World
        </button>
      </Fold>
    </>
  );
};
